import type { OperatorAuditEvent } from '@repo/sdk/gen'
import { zV1BackofficeListAuditEventsData } from '@repo/sdk/zod'
import type { TKey } from '@/lib/i18n'

export type OperatorAuditAction = OperatorAuditEvent['action']

/**
 * The kinds of act the `actions` filter can ask for — read off the
 * request schema rather than the response's, because the two are not the
 * same set. `user.mfa_reset` is real on the response side (an old row from a
 * route MS10 removed can still carry it) but nothing emits a new one, so the
 * filter does not offer it: asking for it on purpose is a question nobody
 * gets to ask, even though seeing it in an unfiltered page is ordinary.
 */
export const AUDIT_ACTIONS = zV1BackofficeListAuditEventsData.shape.query
	.unwrap()
	.shape.actions.unwrap().element.options

/**
 * Action → the words the operator reads. **Total over the response's set**,
 * the wider of the two: `satisfies Record<OperatorAuditAction, TKey>` makes a
 * row this map cannot label a compile error, which is what keeps the legacy
 * `user.mfa_reset` — real on an old row, unreachable through the filter —
 * from printing its raw contract value at somebody.
 */
export const AUDIT_ACTION_LABEL_KEYS = {
	'organization.created': 'operator-audit:action.organization_created',
	'organization.suspended': 'operator-audit:action.organization_suspended',
	'organization.reactivated':
		'operator-audit:action.organization_reactivated',
	'organization.storage_destroyed':
		'operator-audit:action.organization_storage_destroyed',
	'user.invited': 'operator-audit:action.user_invited',
	'user.invite_resent': 'operator-audit:action.user_invite_resent',
	'notification_broadcast.send':
		'operator-audit:action.notification_broadcast_send',
	'user.mfa_reset': 'operator-audit:action.user_mfa_reset',
	'operator.invited': 'operator-audit:action.operator_invited',
	'operator.invitation_resent':
		'operator-audit:action.operator_invitation_resent',
	'operator.invitation_email_changed':
		'operator-audit:action.operator_invitation_email_changed',
	'operator.invitation_revoked':
		'operator-audit:action.operator_invitation_revoked',
	'operator.activated': 'operator-audit:action.operator_activated',
	'operator.deactivated': 'operator-audit:action.operator_deactivated',
	'operator.reactivated': 'operator-audit:action.operator_reactivated',
	'operator.removed': 'operator-audit:action.operator_removed'
} as const satisfies Record<OperatorAuditAction, TKey>

/**
 * What the Dettaglio column says for one row — a translated sentence built
 * from the few keys that kind of act's metadata carries, or a literal the
 * operator themselves typed (a suspension's `reason`), never both and never
 * guessed.
 */
export type AuditDetail =
	| { kind: 'translated'; key: TKey; params: Record<string, unknown> }
	| { kind: 'text'; value: string }

function readString(value: unknown): string | undefined {
	return typeof value === 'string' && value.length > 0 ? value : undefined
}

function readNumber(value: unknown): number | undefined {
	return typeof value === 'number' ? value : undefined
}

/**
 * One builder per action, over the **response's** action set — a row this
 * map has no entry for is the same compile error the label map above catches,
 * so a catalogue addition cannot reach the table with a silent blank.
 *
 * Each reads only the keys `backend/src/modules/audit/constants.ts`
 * documents for that action; a key the catalogue does not declare for this
 * kind of act never reaches the client; the server narrows `metadata`
 * before this cell ever sees it.
 */
const AUDIT_DETAIL_BUILDERS: Record<
	OperatorAuditAction,
	(metadata: Record<string, unknown>) => AuditDetail | null
> = {
	// `durationMs` is an operational figure, not a line worth reading in a
	// log meant for a person — the one thing worth surfacing is *why*, and
	// that only exists on a failed attempt.
	'organization.created': (metadata) => {
		const failureCode = readString(metadata.failureCode)
		return failureCode
			? {
					kind: 'translated',
					key: 'operator-audit:detail.failure_code',
					params: { code: failureCode }
				}
			: null
	},
	// `sessionsRevoked` is implied by the act itself; `reason` is the
	// operator's own words and the only thing worth reading back.
	'organization.suspended': (metadata) => {
		const reason = readString(metadata.reason)
		return reason ? { kind: 'text', value: reason } : null
	},
	// `restoredStatus` restates what "reactivated" already means; `reason`
	// is the one field an operator actually wrote.
	'organization.reactivated': (metadata) => {
		const reason = readString(metadata.reason)
		return reason ? { kind: 'text', value: reason } : null
	},
	// The one irreversible act in the product: both counts are always on
	// the row, so this is the one builder that never answers "nothing to
	// show".
	'organization.storage_destroyed': (metadata) => {
		const nodesDeleted = readNumber(metadata.nodesDeleted)
		const objectVersionsDeleted = readNumber(metadata.objectVersionsDeleted)
		if (nodesDeleted === undefined || objectVersionsDeleted === undefined) {
			return null
		}
		return {
			kind: 'translated',
			key: 'operator-audit:detail.storage_destroyed',
			params: { nodes: nodesDeleted, versions: objectVersionsDeleted }
		}
	},
	// `inviteId` and `role` are the invitation's own identifiers; the role is
	// always the organization's administrator on this surface, so the row's
	// action and organization already say everything worth reading.
	'user.invited': () => null,
	// `resendCount` is which attempt this is — always on the row.
	'user.invite_resent': (metadata) => {
		const resendCount = readNumber(metadata.resendCount)
		return resendCount === undefined
			? null
			: {
					kind: 'translated',
					key: 'operator-audit:detail.invite_resent',
					params: { count: resendCount }
				}
	},
	// Sourced from `admin_audit_log`, whose stored metadata is documented as
	// always empty for the actions this endpoint shows — so there is never a
	// key here worth reading.
	'notification_broadcast.send': () => null,
	'user.mfa_reset': () => null,
	// Acts on the console's own operators. The row's action and target
	// already name who and what; the metadata carries nothing worth reading.
	'operator.invited': () => null,
	'operator.invitation_resent': () => null,
	'operator.invitation_email_changed': () => null,
	'operator.invitation_revoked': () => null,
	'operator.activated': () => null,
	'operator.deactivated': () => null,
	'operator.reactivated': () => null,
	'operator.removed': () => null
}

/** The Dettaglio column's content for one row, or `null` for "nothing here". */
export function auditDetailFor(event: OperatorAuditEvent) {
	return AUDIT_DETAIL_BUILDERS[event.action](event.metadata ?? {})
}
